import React, { Suspense } from "react";
import "./App.css";
import { Login } from "./components/login/Login";
import "bootstrap/dist/css/bootstrap.min.css";
import {
	AppState,
	BoardDataMap,
	ErrHistoryProps,
	ExternalApiKeysProps,
	HomeProps,
	LayoutProps,
	LinkFormProps,
	LinkTableProps,
	LoginProps,
	RepoDataMap,
} from "./types/appTypes";
import { Navigate, Route, Routes } from "react-router-dom";
import Layout from "./components/layout/Layout";
import Home from "./components/Home";
import { KanbanizeBoard, KanbanizeCredentials, KanbanizeTokenObj, RepoLink, Repository } from "./types/kanbanizeTypes";
import { updateDomain, updateCurrentConfig, updateUserToken } from "./utils/store";
import { Container, Spinner } from "react-bootstrap";
import { doPost, doPut, getUserNameByCode, refreshBoards, refreshRepolinksAndRepos } from "./utils/utils";
import { scheduleWarmPostLoginChunks } from "./utils/chunkWarmup";

// PERF-H8: LinkTable, ErrorHistory and ExternalApiKeys are the only three importers of
// material-react-table, which itself pulls in ~81 KB gzip of @mui/material (see
// src/components/tableTheme.tsx). React.lazy()'s `import(...)` is a genuine dynamic import, so
// webpack puts each of these -- and everything only THEY import, including MRT/MUI -- into its
// own chunk instead of the entry chunk. A visitor who only logs in (and never opens one of these
// three screens) now never downloads MRT or MUI at all. Do not change these back to static
// `import X from "./..."` -- that silently undoes the whole split with no build error to catch it
// (see entryChunkGuard.test.ts).
const LinkTable = React.lazy(() => import("./components/linkTable/LinkTable"));
const ErrorHistory = React.lazy(() => import("./components/errorHistory/ErrorHistory"));
const ExternalApiKeys = React.lazy(() => import("./components/externalApiKeys/ExternalApiKeys"));

// PERF-H9: LinkForm is a fourth screen split off the entry chunk, for a different reason than the
// three above -- its weight isn't MUI/MRT, it's the two react-select imports at the top of
// LinkForm.tsx (plain `Select` plus the `react-select/async` variant, used across all four wizard
// steps). Splitting THIS import is only half of what actually removes react-select from the entry
// chunk, though: Layout.tsx renders on every route and has its own react-select instance for the
// corporate org picker (conditionally rendered, but statically imported before PERF-H9), so
// Layout.tsx needed its own matching React.lazy() boundary -- see OrgSelect.tsx and the comment in
// Layout.tsx. Without BOTH boundaries, react-select stays in the entry chunk regardless of which
// one you fix. Do not change this back to a static `import LinkForm from "./..."` (see
// entryChunkGuard.test.ts, extended for PERF-H9).
const LinkForm = React.lazy(() => import("./components/linkForm/LinkForm"));

// Shared fallback for the Suspense boundary below, while one of the four lazy screens' chunks
// (LinkTable/ErrorHistory/ExternalApiKeys from PERF-H8, LinkForm from PERF-H9) is still
// downloading. Only ever visible briefly, on first navigation to that screen.
const routeSuspenseFallback = (
	<div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
		<Spinner animation="border" variant="primary" role="status">
			<span className="visually-hidden">Loading...</span>
		</Spinner>
	</div>
);

// App's OAuth-loading indicator (PERF-H8), shown in render() below while the GitHub code exchange
// is in flight. Used to be MUI's <Box>/<CircularProgress> -- the only usage site of either symbol
// in the whole codebase -- which pulled ~81 KB gzip of @mui/material into the entry chunk just for
// this one spinner. react-bootstrap's <Spinner> is already loaded by every other screen in this
// app, so this swap costs nothing extra. See App.oauthLoadingSpinner.test.tsx, which pins this as
// behaviourally equivalent to the old <CircularProgress>.
const oauthLoadingFallback = (
	<div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
		<Spinner animation="border" variant="primary" role="status">
			<span className="visually-hidden">Loading...</span>
		</Spinner>
	</div>
);

/**
 * Whether this build of the shared SPA targets the GitHub integration (H14).
 *
 * Only that backend carries the Businessmap credentials across the OAuth redirect and reports
 * `businessmap_authenticated`, so only that build can stop persisting the raw owner-level api key.
 */
function isGitHubIntegration(): boolean {
	return process.env.REACT_APP_INTEGRATION_SERVICE === "GitHub";
}

/**
 * Drops a `bm_apikey` left behind by an earlier build (H14).
 *
 * Without this, a tab that already stored the plaintext owner-level key before this change keeps it until an
 * explicit logout -- so upgrading the bundle would not actually clear the exposure for anyone mid-session.
 */
function purgeStoredApiKey(): void {
	if (isGitHubIntegration()) sessionStorage.removeItem("bm_apikey");
}

export default class App extends React.Component<any, AppState> {
	// Captured before React Router can rewrite the URL via Navigate/useLayoutEffect
	private readonly oauthSearch: string;
	// True when the page loaded as the OAuth provider's redirect callback
	private readonly isOAuthCallback: boolean;

	// One-shot guard against replaying the SAME GitHub OAuth `code` through a second exchange.
	// Deliberately STATIC (module/class-level, not an instance field): an instance field would
	// reset the moment a brand new App instance is constructed, and there are two distinct ways
	// that happens for the exact same still-present `?code&state` pair without any real page
	// navigation in between:
	//   1. React 18 StrictMode's documented double-invoke (see App.oauthDoubleInvoke.test.tsx) -
	//      componentDidMount runs twice, but on the SAME surviving instance, so an instance field
	//      would actually have worked for this one case.
	//   2. ErrorBoundary's "Try again" control (src/components/ErrorBoundary.tsx) unmounts App
	//      entirely on a render-phase error and remounts it FROM SCRATCH - a brand new instance,
	//      a brand new constructor call - while the URL can still carry the old ?code&state. An
	//      instance field resets right along with that new instance and would not have helped
	//      here, which is why this has to live above the instance.
	// A genuinely NEW code (an actual second OAuth round trip, initiated by the user) only ever
	// arrives via a real browser navigation back to this page - which reloads the whole JS module
	// (this field included) from scratch - so this can never permanently latch the SPA out of a
	// legitimate later login; it only ever blocks a replay of the exact code already spent.
	private static exchangedOAuthCode: string | undefined;

	constructor(props: any) {
		super(props);

		// Must read search params here — Navigate fires its useLayoutEffect before
		// componentDidMount, so window.location.search may already be empty by then.
		this.oauthSearch = window.location.search;
		const params = new URLSearchParams(this.oauthSearch);
		this.isOAuthCallback = params.has("username") || params.has("code");

		// For non-GitHub services the username comes directly in the URL.
		const initialUserName =
			process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub"
				? params.get("username") || undefined
				: undefined;

		const appState: AppState = {
			integrationApiUrl: process.env.REACT_APP_INTEGRATION_URL as string,
			kanbanizeDomain: sessionStorage.getItem("bm_domain") || undefined,
			kanbanizeApiKey: undefined,
			currentlyEditingRepoName: undefined,
			refresh_repos_on_org_change: true,
			// On OAuth callback do NOT restore the old token — it was created before the
			// Git OAuth session existed, so the backend would reject it. componentDidMount
			// will re-issue a fresh combined token using the stored credentials.
			userToken: this.isOAuthCallback ? undefined : (sessionStorage.getItem("bm_token") || undefined),
			boards: [],
			listConfig: [],
			repositories: [],
			currentConfig: undefined,
			generalError: false,
			userName: initialUserName,
			boardDataMap: {},
			repoDataMap: {},
			corporateUserOrgs: [],
			currentUserOrg: "",
			current_component: "",
			refreshing_repos_org_change: false,
			isOAuthLoading: this.isOAuthCallback,
		};

		this.state = appState;
		this.setUserToken.bind(this);
		this.logOut.bind(this);
		this.setKanbanizeDomain.bind(this);
		this.setListConfig.bind(this);
		this.getLoginProps.bind(this);
		this.getLinkTableProps.bind(this);
		this.getLinkFormProps.bind(this);
		this.getUserName.bind(this);
	}

	async componentDidMount() {
		purgeStoredApiKey();
		document.title = `Businessmap to ${
			process.env.REACT_APP_INTEGRATION_SERVICE ? process.env.REACT_APP_INTEGRATION_SERVICE : ""
		}${process.env.REACT_APP_INTEGRATION_VERSION ? " (" + process.env.REACT_APP_INTEGRATION_VERSION + ")" : ""} integration`;
		try {
			// Use the search string captured in the constructor so GitHub's async code
			// exchange still works even if routing has already rewritten window.location.
			const businessmapAuthenticated = await this.getUserName(this.oauthSearch);
			// After an OAuth redirect the Git session is now established on the backend.
			if (this.isOAuthCallback) {
				const domain = sessionStorage.getItem("bm_domain");
				if (isGitHubIntegration()) {
					// H14: no key to replay. The backend preserved the tenant credentials across the redirect and told
					// us so, which is the whole reason the raw owner-level key no longer has to survive in
					// sessionStorage. The restored token is the same opaque placeholder /api/login returns -- real
					// authentication is the httpOnly session cookie, never this value.
					const storedToken = sessionStorage.getItem("bm_token");
					if (businessmapAuthenticated && storedToken) {
						this.setState({ userToken: storedToken, kanbanizeDomain: domain || undefined });
					}
				} else {
					// Unchanged for the other integrations: their backends do not report the flag yet, so the stored
					// key is still what re-establishes the Businessmap half after the redirect.
					const apiKey = sessionStorage.getItem("bm_apikey");
					if (domain && apiKey) {
						await this.getUserToken(domain, apiKey);
					}
				}
			}
		} catch (e: any) {
			// Without this, a rejection from getUserName()/doPost (e.g. the backend's session-
			// validation or rate-limit envelope, now that utils.ts's fetch helpers reject on it
			// instead of silently resolving it as data) escaped uncaught: isOAuthLoading still
			// cleared via `finally` below, but setGeneralError was never called, so the user landed
			// on a bare login form with no explanation for why the OAuth exchange failed.
			const message = e && typeof e.message === "string" && e.message.trim().length > 0
				? e.message
				: "Something went wrong completing the sign-in. Please try again.";
			this.setGeneralError(message);
		} finally {
			if (this.isOAuthCallback) {
				this.setState({ isOAuthLoading: false });
			}
		}
		// Covers the "already authenticated on load" case (a returning visitor whose bm_token/
		// bm_domain were already in sessionStorage, restored straight into the constructor's initial
		// state - see AppState.userToken above) - for that visitor, nothing below sets state again
		// after this point, so componentDidUpdate's own call to this (below) would never fire on its
		// own without this one.
		this.maybeWarmPostLoginChunks();
	}

	/**
	 * PERF-H9 (prefetch pairing). `/currentLinks` is where every authenticated visitor lands
	 * immediately after login (see the routes in render() below), and for corporate visitors
	 * Layout's org-select chunk renders alongside it right away too. Left as a bare lazy split,
	 * that's only a DEFERRAL of the MUI/MRT/react-select download for this visitor, not a removal
	 * of it - the same bytes still block the first screen they actually look at, just one
	 * navigation later. This schedules both chunks to warm at idle time (see
	 * src/utils/chunkWarmup.ts) the moment this component knows the visitor is authenticated, so
	 * they are typically already cached by the time <LinkTable>/<OrgSelect> actually mount.
	 * Deliberately does NOT warm LinkForm's, ErrorHistory's or ExternalApiKeys' chunks - those are
	 * reached by a user action from the table (Create new link / Error history / API keys), not
	 * automatically on login, so warming them here would spend bytes most sessions never need.
	 */
	maybeWarmPostLoginChunks = (): void => {
		if (this.state.userToken || this.state.corporateUserOrgs.length) {
			scheduleWarmPostLoginChunks(this.state.corporateUserOrgs.length > 0);
		}
	};

	// Authentication (userToken / corporateUserOrgs) is only ever established asynchronously, after
	// mount - the OAuth exchange in componentDidMount above, or a plain (non-GitHub) login via
	// getUserToken(). componentDidUpdate is what catches THOSE transitions; the call at the end of
	// componentDidMount above only covers a visitor who was already authenticated at mount time.
	// scheduleWarmPostLoginChunks's own one-shot guard makes every call after the first a no-op, so
	// firing this on every update (rather than diffing prev/next auth state here as well) is
	// deliberately simple, per the review's "do not build a prefetch framework" guidance.
	componentDidUpdate(): void {
		this.maybeWarmPostLoginChunks();
	}

	setCorporateUserOrgs = (corporateUserOrgs: string[]) => {
		this.setState({ corporateUserOrgs, currentUserOrg: this.state.currentUserOrg || corporateUserOrgs[0] });
	};

	changeCorporateUserOrg = async (user_org: string) => {
		console.log("changeCorporateUserOrg: init");
		try {
			// Call backend to update current user organization.
			await doPut<{ current_org: string }>(`${this.state.integrationApiUrl}/api/login/corporate/current_org/${user_org}`, {});
			this.setState({ currentUserOrg: user_org, listConfig: [], refreshing_repos_org_change: true }, async () => {
				// This callback runs after the setState above has already committed, outside the try
				// block that wraps this function's own body - a `try` around `setState(state, cb)` does
				// NOT catch anything thrown asynchronously inside `cb` later on. It needs its own
				// try/catch, or a rejection here becomes an unhandled promise rejection that leaves
				// `refreshing_repos_org_change` stuck `true` (LinkTable renders that as a permanently
				// spinning table) and the org selector silently reverting to its previous value.
				try {
					// PERF-H10: `user_org` (this function's own argument, just committed above as
					// `currentUserOrg`) rather than re-reading `this.state.currentUserOrg` - it is the
					// exact org these two calls are for, without depending on whether React has
					// finished flushing the setState above by the time this callback runs. LinkTable's
					// own load (LinkTable.tsx) is keyed the same way, off `this.props.currentUserOrg`,
					// so a concurrent call from either side for the SAME org dedupes to one request
					// (see utils.ts's dedupeInFlightRequest) and a call for a DIFFERENT org never does.
					const boards = await refreshBoards(this.state.integrationApiUrl, this.state.userToken, user_org);
					const repolinks_repos = await refreshRepolinksAndRepos(
						this.state.integrationApiUrl,
						this.state.kanbanizeDomain || "",
						this.state.userToken,
						user_org,
					);
					this.setState({
						repositories: repolinks_repos.allRepos,
						listConfig: repolinks_repos.savedConfigs,
						boards,
						refreshing_repos_org_change: false,
					});
				} catch (e: any) {
					this.setState({ refreshing_repos_org_change: false }, () => this.setGeneralError(e.message));
				}
			});
		} catch (e: any) {
			this.setGeneralError(e.message);
		}
	};

	setCurrentComponent = (component: string) => {
		this.setState({ current_component: component });
	};

	addBoardDataMap = (board: string, dataMap: BoardDataMap["key"]) => {
		let currentState = this.state.boardDataMap;
		if (!currentState) currentState = {};
		currentState[board] = dataMap;
		this.setState({ boardDataMap: currentState });
	};

	addRepoDataMap = (repo: string, dataMap: RepoDataMap["key"]) => {
		let currentState = this.state.repoDataMap;
		if (!currentState) currentState = {};
		currentState[repo] = dataMap;
		this.setState({ repoDataMap: currentState });
	};

	setListConfig = (listConfig: RepoLink[]) => {
		this.setState({ listConfig });
	};

	setRepositories = (repositories: Repository[]) => {
		this.setState({ repositories });
	};

	setBoards = (boards: KanbanizeBoard[]) => {
		this.setState({ boards });
	};

	/**
	 * Completes the OAuth callback and reports whether the backend session already holds this tenant's
	 * Businessmap credentials (H14) -- a boolean about the session, never any part of the credential itself.
	 */
	getUserName = async (search: string): Promise<boolean> => {
		if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
			const username = new URLSearchParams(search).get("username");
			if (username) {
				this.setState({ userName: username });
			}
		} else {
			const code = new URLSearchParams(search).get("code");
			const state = new URLSearchParams(search).get("state");
			if (code) {
				if (App.exchangedOAuthCode === code) {
					// Same code already exchanged (see the field's doc comment above) - a genuine
					// no-op. No network call, and nothing below runs, so userName/userToken/
					// kanbanizeDomain are left exactly as the original, successful exchange set
					// them (or, on a genuinely failed first exchange, this call simply does not
					// pile a second, guaranteed-to-fail request and a second, redundant error
					// banner on top of the one the first attempt already raised - the backend
					// only issues a fresh code per real OAuth round trip anyway, so replaying this
					// one could never succeed).
					return false;
				}
				// Recorded BEFORE the exchange is awaited, not after it resolves: React
				// StrictMode's second componentDidMount call runs on this SAME instance and can
				// start before the first call's `await getUserNameByCode(...)` below has settled,
				// so the guard has to already be visible to it at that point, not once the network
				// round trip finishes.
				App.exchangedOAuthCode = code;
				const data = await getUserNameByCode(this.state.integrationApiUrl as string, code, state);
				if (data.user_orgs !== undefined) {
					if (data.user_orgs.length === 0) {
						const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
						this.setGeneralError(
							`You do not belong to any of the allowed corporate business. ` +
							`If you believe you should be able to access, please check: ` +
							`you are a member of the organization at ${integration}, and the organization ` +
							`has been added to the corporate login list by our team. If not, please contact support.`
						);
						return false;
					}
					this.setCorporateUserOrgs(data.user_orgs);
					// MUST be awaited, not fired-and-forgotten. This issues a PUT to
					// `/api/login/corporate/current_org/:name`, and on the backend that handler calls
					// `session.regenerate()` - it destroys the CURRENT session id and mints a brand new
					// one, sent back as a fresh `connect.sid` cookie on the PUT's response. componentDidMount
					// below only flips `isOAuthLoading` to `false` (and lets the real Routes/`<Navigate>`
					// tree, including LinkTable, render for the first time) once the promise this
					// function returns has settled. Leaving this call unawaited let that render happen
					// while the PUT was still in flight: LinkTable mounted and fired its own
					// GET /api/availableBoards carrying the OLD cookie, which the server had already
					// destroyed - a 401 ("Incorrect request!") - a beat before the legitimate request
					// (from this function's own setState callback, once the PUT truly resolves)
					// succeeded with the new one.
					await this.changeCorporateUserOrg(data.current_org);
				}
				this.setState({ userName: data.username });
				return data.businessmap_authenticated === true;
			}
		}
		return false;
	};

	setUserToken = (token: string) => {
		this.setState({ userToken: token }, () => {
			updateUserToken(token);
		});
	};

	logOut = () => {
		sessionStorage.removeItem("bm_token");
		sessionStorage.removeItem("bm_domain");
		sessionStorage.removeItem("bm_apikey");
		this.setState({ userToken: undefined, userName: undefined, corporateUserOrgs: [] });
	};

	setKanbanizeDomain = (domain: string) => {
		this.setState({ kanbanizeDomain: domain }, () => {
			updateDomain(domain);
		});
	};

	setKanbanizeApiKey = (apiKey: string) => {
		this.setState({ kanbanizeApiKey: apiKey });
	};

	setCurrentConfig = (currentConfig: RepoLink) => {
		this.setState({ currentlyEditingRepoName: currentConfig.repo_name }); //TODO: add to persistent redux like below.
		this.setState({ currentConfig: currentConfig }, () => {
			updateCurrentConfig(currentConfig);
		});
	};

	setGeneralError = (message: string | false) => {
		this.setState({ generalError: message });
	};

	getLoginProps = () => {
		const loginProps: LoginProps = {
			integrationUrl: this.state.integrationApiUrl || "",
			gh_client_id: process.env.REACT_APP_GH_CLIENT_ID,
			getUserToken: this.getUserToken,
			setGeneralError: this.setGeneralError,
			setCorporateUserOrgs: this.setCorporateUserOrgs,
			setCurrentComponent: this.setCurrentComponent,
			changeCorporateUserOrg: (user_org: string, refresh: boolean) => {
				this.setState({ refresh_repos_on_org_change: refresh }, () => {
					this.changeCorporateUserOrg(user_org);
				});
			},
		};
		return loginProps;
	};

	getErrHistoryProps = () => {
		const historyProps: ErrHistoryProps = {
			integrationUrl: this.state.integrationApiUrl || "",
			currentConfig: this.state.currentConfig || ({} as RepoLink),
			setCurrentComponent: this.setCurrentComponent,
			setGeneralError: this.setGeneralError,
		};
		return historyProps;
	};

	getExternalApiKeysProps = () => {
		const externalApiKeysProps: ExternalApiKeysProps = {
			userToken: this.state.userToken || "",
			integrationApiUrl: this.state.integrationApiUrl || "",
			kanbanizeDomain: this.state.kanbanizeDomain || "",
			userName: this.state.userName || "",
			setCurrentComponent: this.setCurrentComponent,
			setGeneralError: this.setGeneralError,
		};
		return externalApiKeysProps;
	};

	setRefreshingTableByOrgChange = (refreshing: boolean, callback?: () => void) => {
		this.setState({ refreshing_repos_org_change: refreshing }, callback);
	};

	getLinkTableProps = () => {
		const props: LinkTableProps = {
			integrationDomain: this.state.integrationApiUrl || "",
			kanbanizeDomain: this.state.kanbanizeDomain || "",
			// PERF-H10: same value passed to refreshBoards/refreshRepolinksAndRepos below (via
			// changeCorporateUserOrg) - keeps LinkTable's own load on the same in-flight-dedupe key.
			currentUserOrg: this.state.currentUserOrg,
			userToken: this.state.userToken || "",
			listConfig: this.state.listConfig,
			boards: this.state.boards,
			refreshing: this.state.refreshing_repos_org_change,
			setListConfig: this.setListConfig,
			setRefreshingTableByOrgChange: this.setRefreshingTableByOrgChange,
			setCurrentComponent: this.setCurrentComponent,
			setRepositories: this.setRepositories,
			setBoards: this.setBoards,
			setCurrentConfig: this.setCurrentConfig,
			setGeneralError: this.setGeneralError,
		};
		return props;
	};

	getLayoutProps = () => {
		const props: LayoutProps = {
			generalError: this.state.generalError,
			integrationUrl: this.state.integrationApiUrl || "",
			userToken: this.state.userToken || "",
			corporateUserOrgs: this.state.corporateUserOrgs,
			currentUserOrg: this.state.currentUserOrg,
			showOrgSelect: this.state.current_component === "LinkTable",
			setUserToken: this.setUserToken,
			setUserOrg: this.changeCorporateUserOrg,
			logOut: this.logOut,
			setGeneralError: this.setGeneralError,
		};
		return props;
	};

	getHomeProps = () => {
		let oauthUrl = "";
		const integration = process.env.REACT_APP_INTEGRATION_SERVICE;
		if (!integration) throw new Error("Error reading environment (INTEGRATION_SERVICE)");
		oauthUrl = `${process.env.REACT_APP_INTEGRATION_URL}/api/auth/${integration.toLowerCase()}/oauth`;
		const props: HomeProps = {
			oauthUrl,
		};
		return props;
	};

	getLinkFormProps = () => {
		const props: LinkFormProps = {
			integrationUrl: this.state.integrationApiUrl || "",
			editingReponame: this.state.currentlyEditingRepoName || "",
			userToken: this.state.userToken || "",
			currentConfig: this.state.currentConfig || ({} as RepoLink),
			listConfig: this.state.listConfig,
			repositories: this.state.repositories,
			boards: this.state.boards,
			boardDataMap: this.state.boardDataMap,
			repoDataMap: this.state.repoDataMap,
			setCurrentComponent: this.setCurrentComponent,
			setGeneralError: this.setGeneralError,
			addRepoDataMap: this.addRepoDataMap,
			addBoardDataMap: this.addBoardDataMap,
			setBoards: this.setBoards,
			setRepositories: this.setRepositories,
		};
		return props;
	};

	getUserToken = async (kanbanizeDomain: string, apiKey: string) => {
		try {
			if (!kanbanizeDomain || !apiKey) return;
			const credentials: KanbanizeCredentials = {
				kanbanize_domain: kanbanizeDomain,
				api_key: apiKey
			};
			let loginUrl = "";
			if (process.env.REACT_APP_INTEGRATION_SERVICE !== "GitHub") {
				loginUrl = `${this.state.integrationApiUrl}/api/auth/login`;
			} else {
				loginUrl = `${this.state.integrationApiUrl}/api/login`;
			}
			const jsonData: KanbanizeTokenObj = await doPost(loginUrl, credentials, "");
			sessionStorage.setItem("bm_token", jsonData.token);
			sessionStorage.setItem("bm_domain", kanbanizeDomain);
			// H14: the raw Businessmap api key is NEVER written to any Storage API on the GitHub build. It is the
			// tenant's owner-level, first-party REST credential -- usable directly against
			// <tenant>.kanbanize.com/api/v2/*, bypassing this integration entirely -- and it used to sit in
			// sessionStorage in plaintext for the life of the tab, readable by any script in this origin. It now
			// exists only as this function's argument, for the single POST above.
			//
			// The ONLY reason it was persisted was to replay /api/login after the OAuth redirect wiped the page.
			// The GitHub backend now carries the tenant credentials across that redirect server-side (in the
			// httpOnly session) and reports `businessmap_authenticated` on the OAuth callback, so there is nothing
			// left to replay -- see componentDidMount.
			//
			// The other integrations this shared SPA builds for (BitBucket/GitLab/Azure) still need the replay,
			// because their own backends do not report that flag yet; for them the key is still stored, and
			// removing it here would break their post-redirect re-login. Those backends have to add the same two
			// things before this branch can go away.
			if (!isGitHubIntegration()) sessionStorage.setItem("bm_apikey", apiKey);
			this.setState({ userToken: jsonData.token, kanbanizeDomain: kanbanizeDomain });
		} catch (e: any) {
			this.setState({ generalError: e.message });
		}
	};

	render(): React.ReactNode {
		if (this.state.isOAuthLoading) {
			return oauthLoadingFallback;
		}
		return (
			<Container>
				<Suspense fallback={routeSuspenseFallback}>
					<Routes>
						<Route path="/" element={<Layout {...this.getLayoutProps()} />}>
							<Route index element={<Navigate to="/home" replace={true} />} />
							<Route
								path="/home"
								element={
									(!!this.state.corporateUserOrgs.length && <Navigate to="/currentLinks" replace={true} />) ||
									(this.state.userToken && <Navigate to="/login" replace={true} />) ||
									<Login {...this.getLoginProps()} />
								}
							/>
							<Route
								path="/login"
								element={
									(this.state.userName && <Navigate to="/currentLinks" replace={true} />) ||
									(this.state.userToken && <Home {...this.getHomeProps()} />) ||
									<Navigate to="/home" replace={true} />
								}
							/>
							<Route
								path="/currentLinks"
								element={
									((this.state.userToken || this.state.corporateUserOrgs.length) && (
										<LinkTable {...this.getLinkTableProps()} />
										// <Test />
									)) || <Navigate to="/home" replace={true} />
								}
							/>
							<Route
								path="/linkForm"
								element={
									((this.state.userToken || this.state.corporateUserOrgs.length) && (
										<LinkForm {...this.getLinkFormProps()} />
									)) || <Navigate to="/home" replace={true} />
								}
							/>
							<Route
								path="/errHistory"
								element={
									((this.state.userToken || this.state.corporateUserOrgs.length) && (
										<ErrorHistory {...this.getErrHistoryProps()} />
										// <Test />
									)) || <Navigate to="/home" replace={true} />
								}
							></Route>
							<Route
								path="/externalApiKeys"
								element={
									((this.state.userToken || this.state.corporateUserOrgs.length) && (
										<ExternalApiKeys {...this.getExternalApiKeysProps()} />
									)) || <Navigate to="/home" replace={true} />
								}
							></Route>
						</Route>
					</Routes>
				</Suspense>
			</Container>
		);
	}
}
